// Derives a uniform list of "change entries" from the policy-change form
// values, across all section shapes (record arrays + single objects), so the
// ChangesRequested summary can render them the same way regardless of section.

import { IAutomobilePolicyState } from "../../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";
import { ChangeSectionKey } from "./WhatChangedBoard";

export interface IChangeFieldRow {
  label: string;
  from?: React.ReactNode;
  to?: React.ReactNode;
  // Whether this field is required but empty (drives "fields missing").
  missing?: boolean;
}

export interface IChangeEntry {
  // Unique id for keying / edit / remove.
  id: string;
  section: ChangeSectionKey;
  subType: string;
  mode: string; // ADD / CHANGE / DELETE
  title: string;
  fields: IChangeFieldRow[];
  missingCount: number;
  // For record-array sections, the index of the record; undefined for single-object sections.
  recordIndex?: number;
}

const asText = (v: any): string => {
  if (v === null || v === undefined || v === "") {
    return "";
  }
  return String(v);
};

const buildFields = (rows: IChangeFieldRow[]): IChangeFieldRow[] =>
  rows.map((r) => ({ ...r, missing: r.missing ?? asText(r.to) === "" }));

const countMissing = (fields: IChangeFieldRow[]): number =>
  fields.filter((f) => f.missing).length;

export const deriveChangeEntries = (
  values: IAutomobilePolicyState
): IChangeEntry[] => {
  const entries: IChangeEntry[] = [];

  // --- Others: LOA / LOE / Other request type ---
  // The "Others" board card records a type-level request (LOA / LOE / General
  // Change) that has no dedicated field form. It shows whenever the request
  // `type` has been switched away from the default V2 "PolicyChange".
  const OTHER_REQUEST_TYPE_LABELS: Record<string, string> = {
    LOA: "LOA",
    LOE: "LOE",
    GeneralChange: "Other (General Change)",
    New: "New (upload)",
    "Remarket(upload)": "Remarket (upload)",
  };
  if (values.type && OTHER_REQUEST_TYPE_LABELS[values.type]) {
    const label = OTHER_REQUEST_TYPE_LABELS[values.type];
    entries.push({
      id: "other_request_type",
      section: "otherRequestType",
      subType: values.sub_type || label,
      mode: "REQUEST",
      title: label,
      fields: [],
      missingCount: 0,
    });
  }

  // --- Named Insured (single object) ---
  if (values.name_change && asText(values.name_change.new_name) !== "") {
    const fields = buildFields([
      { label: "New name", to: values.name_change.new_name },
      { label: "Date of birth", to: values.name_change.date_of_birth },
      { label: "Relationship", to: values.name_change.relationship },
    ]);
    entries.push({
      id: "name_change",
      section: "nameChange",
      subType: "Name Change",
      mode: "CHANGE",
      title: `Named insured ${values.name_change.new_name || ""} — CHANGE`.trim(),
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Address (single object) ---
  // V2 lets the user record an address change purely via the pill selector
  // (Mailing/Garaging/Both), contact fields, or per-vehicle re-rate rows,
  // without necessarily filling the free-text `address` field (e.g. a
  // garaging-only re-rate with no mailing address change). So this entry
  // shows whenever ANY of those fields carry data, not just `address`.
  const addressChange = values.address_change;
  const hasVehicleUpdates = (addressChange?.vehicle_updates || []).some(
    (update) => update.selected
  );
  const hasAddressChangeData =
    addressChange &&
    [
      addressChange.address,
      addressChange.change_type,
      addressChange.unit_number,
      addressChange.city,
      addressChange.state,
      addressChange.postal_code,
      addressChange.phone,
      addressChange.email,
    ].some((v) => asText(v) !== "");
  if (hasAddressChangeData || hasVehicleUpdates) {
    // Each selected vehicle gets its own single-line row summarising exactly
    // what changed for it (parking / commute / annual km / use / principal
    // driver / occasional driver), rather than one combined "list of
    // numbers" row — matches how every other field is shown one-per-line.
    const vehicleFields: IChangeFieldRow[] = (addressChange.vehicle_updates || [])
      .filter((update) => update.selected)
      .map((update) => {
        const changedParts = [
          update.parking && `Parking: ${update.parking}`,
          update.commute && `Commute: ${update.commute}`,
          update.annual_km && `Annual km: ${update.annual_km}`,
          update.use && `Use: ${update.use}`,
          update.principal_driver && `Principal driver: ${update.principal_driver}`,
          update.occasional_driver && `Occasional driver: ${update.occasional_driver}`,
        ].filter(Boolean);
        return {
          label: `Vehicle ${update.vehicle_number ?? "--"}`,
          to: changedParts.length > 0 ? changedParts.join(", ") : "No re-rate fields changed",
          missing: false,
        };
      });

    const fields = buildFields([
      { label: "Change type", to: addressChange.change_type },
      { label: "Address", to: addressChange.address },
      { label: "Unit", to: addressChange.unit_number, missing: false },
      { label: "City", to: addressChange.city },
      { label: "Province/State", to: addressChange.state },
      { label: "Postal code", to: addressChange.postal_code },
      { label: "Phone", to: addressChange.phone, missing: false },
      { label: "Email", to: addressChange.email, missing: false },
      ...vehicleFields,
    ]);
    entries.push({
      id: "address_change",
      section: "addressChange",
      subType: "Address Change",
      mode: "CHANGE",
      title: "Address — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Payment Method / Credit Card (single object) ---
  const cc = values.credit_card_info;
  const hasPaymentInfo =
    cc &&
    [
      cc.payment_type,
      cc.cc,
      cc.expiry,
      cc.cvv,
      cc.account_holder_name,
      cc.bank_name,
      cc.account_number,
      cc.institution_number,
      cc.transit_number,
    ].some((v) => asText(v) !== "");
  if (hasPaymentInfo) {
    // Human-readable label for the chosen payment type + authorization.
    const paymentTypeLabel =
      cc.payment_type === "Bank"
        ? "Bank (PAD)"
        : cc.payment_type === "Credit Card"
        ? "Credit card"
        : cc.payment_type || "";
    const authLabel =
      cc.signature_type === "G4Sign"
        ? "Sign via G4 Sign"
        : cc.signature_type === "OnFile"
        ? "On file"
        : null;
    const maskedCard =
      asText(cc.cc) !== "" ? `•••• ${asText(cc.cc).slice(-4)}` : "";

    // Only show the fields relevant to the selected payment type, so the
    // summary matches what the user actually filled in.
    let typeFields: { label: string; to: any; missing?: boolean }[] = [];
    if (cc.payment_type === "Bank") {
      typeFields = [
        { label: "Bank name", to: cc.bank_name },
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Institution number", to: cc.institution_number },
        { label: "Transit number", to: cc.transit_number },
        { label: "Account number", to: cc.account_number },
      ];
    } else if (cc.payment_type === "Credit Card") {
      typeFields = [
        { label: "Card number", to: maskedCard, missing: false },
        { label: "Expiry", to: cc.expiry },
        { label: "Account holder name", to: cc.account_holder_name },
      ];
    } else {
      // Cheque / Other / not yet chosen.
      typeFields = [
        { label: "Account holder name", to: cc.account_holder_name },
        { label: "Bank name", to: cc.bank_name },
      ];
    }

    const fields = buildFields([
      { label: "Payment type", to: paymentTypeLabel, missing: false },
      ...typeFields,
      ...(authLabel
        ? [{ label: "Authorization", to: authLabel, missing: false }]
        : []),
    ]);
    entries.push({
      id: "payment_method_change",
      section: "paymentMethodChange",
      subType: "Payment Method Change",
      mode: "CHANGE",
      title: `Payment method${
        paymentTypeLabel ? ` (${paymentTypeLabel})` : ""
      } — CHANGE`,
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Cancellation / Reinstatement (single object) ---
  // Captured via the "Cancellation / Reinstatement" board card: a Cancel
  // Reason (cancellation_reason) and a Subtype (sub_type: FLAT / SHORT RATE /
  // PRO-RATA). Shows whenever either has been chosen.
  const CANCELLATION_SUBTYPES = ["FLAT", "SHORT RATE", "PRO-RATA"];
  const hasCancellationSubType =
    CANCELLATION_SUBTYPES.includes(asText(values.sub_type));
  if (asText(values.cancellation_reason) !== "" || hasCancellationSubType) {
    const fields = buildFields([
      { label: "Cancel Reason", to: values.cancellation_reason },
      {
        label: "Subtype",
        to: hasCancellationSubType ? values.sub_type : null,
      },
    ]);
    entries.push({
      id: "cancellation_reinstatement",
      section: "cancellationReinstatement",
      subType: "Cancellation/Reinstatement",
      mode: "CHANGE",
      title: "Cancellation / Reinstatement — CHANGE",
      fields,
      missingCount: countMissing(fields),
    });
  }

  // --- Driver (record array) ---
  // A "Change" (substitution) replaces an existing driver (tracked in
  // delete_driver) with a newly added record, so those records show as
  // CHANGE with the replaced driver called out. Plain additions still show
  // as ADD.
  // Per-record mode: each record keeps its own "Add"/"Change" mode (stamped at
  // save time) so adding a new "Add" driver never retroactively flips earlier
  // "Change" records to ADD. Falls back to the section `type` for legacy
  // records saved before per-record mode existed.
  const sectionIsDriverChange = values.driver_change?.type === "Change";
  (values.driver_change?.records || []).forEach((rec: any, index) => {
    const isDriverSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsDriverChange;
    const mode = isDriverSubstitution ? "CHANGE" : "ADD";
    const replacedDriver =
      rec.replaced_record_details ?? values.driver_change?.delete_driver_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedDriver?.driver_number ??
      values.driver_change?.delete_driver;
    const fields = buildFields([
      // The driver being replaced (removed): number, name and license.
      ...(isDriverSubstitution
        ? [
            {
              label: "Driver to replace — number",
              to: replacedNumber,
              missing: false,
            },
            {
              label: "Driver to replace — name",
              to: replacedDriver?.driver_name ?? null,
              missing: false,
            },
            {
              label: "Driver to replace — license",
              to: replacedDriver?.license_number ?? null,
              missing: false,
            },
          ]
        : []),
      // The new driver being added in its place.
      { label: isDriverSubstitution ? "New driver — number" : "Driver number", to: rec.driver_number },
      { label: isDriverSubstitution ? "New driver — name" : "Driver name", to: rec.driver_name },
      { label: isDriverSubstitution ? "New driver — license" : "License number", to: rec.license_number },
    ]);
    entries.push({
      id: `driver_change-${index}`,
      section: "driverChange",
      subType: "Driver Change",
      mode,
      title: `Driver ${rec.driver_name || `#${index + 1}`} — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  // A separate DELETE entry when a driver has been selected for deletion.
  if (
    !sectionIsDriverChange &&
    asText(values.driver_change?.delete_driver) !== ""
  ) {
    const fields = buildFields([
      { label: "Driver", to: values.driver_change.delete_driver, missing: false },
    ]);
    entries.push({
      id: "driver_change-delete",
      section: "driverChange",
      subType: "Driver Change",
      mode: "DELETE",
      title: `Driver ${values.driver_change.delete_driver} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Vehicle (record array) ---
  // A "Change" (substitution) replaces an existing vehicle (tracked in
  // delete_vehicle) with a newly added record, so those records show as
  // CHANGE with the replaced vehicle called out. Plain additions still show
  // as ADD. This only applies when the section mode isn't Delete (a
  // standalone Delete never has records).
  // Per-record mode (see Driver note above).
  const sectionIsVehicleChange = values.vehicle_change?.type === "Change";
  (values.vehicle_change?.records || []).forEach((rec: any, index: number) => {
    const label = [rec.year, rec.make, rec.model].filter(Boolean).join(" ");
    const isVehicleSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsVehicleChange;
    const mode = isVehicleSubstitution ? "CHANGE" : "ADD";
    const replacedVehicle =
      rec.replaced_record_details ??
      values.vehicle_change?.delete_vehicle_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedVehicle?.vehicle_number ??
      values.vehicle_change?.delete_vehicle;
    const fields = buildFields([
      // The vehicle being replaced (removed): number, year/make/model, VIN.
      ...(isVehicleSubstitution
        ? [
            {
              label: "Vehicle to replace — number",
              to: replacedNumber,
              missing: false,
            },
            {
              label: "Vehicle to replace — year/make/model",
              to:
                [
                  replacedVehicle?.year,
                  replacedVehicle?.make,
                  replacedVehicle?.model,
                ]
                  .filter(Boolean)
                  .join(" ") || null,
              missing: false,
            },
            {
              label: "Vehicle to replace — VIN",
              to: replacedVehicle?.vin ?? null,
              missing: false,
            },
          ]
        : []),
      // The new vehicle being added in its place.
      ...(isVehicleSubstitution
        ? [{ label: "New vehicle — number", to: rec.incremental_number ?? rec.vehicle_number }]
        : []),
      { label: isVehicleSubstitution ? "New vehicle — year" : "Year", to: rec.year },
      { label: isVehicleSubstitution ? "New vehicle — make" : "Make", to: rec.make },
      { label: isVehicleSubstitution ? "New vehicle — model" : "Model", to: rec.model },
      { label: isVehicleSubstitution ? "New vehicle — VIN" : "VIN", to: rec.vin },
    ]);
    entries.push({
      id: `vehicle_change-${index}`,
      section: "vehicleChange",
      subType: "Vehicle Change",
      mode,
      title: `Vehicle ${label || `#${index + 1}`} — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  if (
    !sectionIsVehicleChange &&
    asText(values.vehicle_change?.delete_vehicle) !== ""
  ) {
    const fields = buildFields([
      { label: "Vehicle", to: values.vehicle_change.delete_vehicle, missing: false },
    ]);
    entries.push({
      id: "vehicle_change-delete",
      section: "vehicleChange",
      subType: "Vehicle Change",
      mode: "DELETE",
      title: `Vehicle ${values.vehicle_change.delete_vehicle} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Coverage (record array) ---
  // A "Change" (substitution) replaces an existing coverage (tracked in
  // delete_coverage) with a newly added record, so those records show as
  // CHANGE with the replaced coverage called out. Plain additions show as ADD.
  // Per-record mode (see Driver note above).
  const sectionIsCoverageChange = values.coverage_change?.type === "Change";
  (values.coverage_change?.records || []).forEach((rec: any, index: number) => {
    // The coverage form stores the selected coverage in `coverage_name`, but
    // it can be identified by `coverage_number` too. Fall back so the summary
    // never shows a blank/"Missing" coverage when a number/VIN is present.
    const coverageDisplay =
      asText(rec.coverage_name) !== ""
        ? rec.coverage_name
        : asText(rec.coverage_number) !== ""
        ? rec.coverage_number
        : rec.vin;
    // The form shows a calculated "Total Premium" but doesn't persist it into
    // `total_premium`. Derive it from the coverage line items (sum of
    // `premimum`) so the summary reflects the same value the form displays.
    const derivedTotalPremium =
      rec.total_premium ??
      (Array.isArray(rec.coverage_details)
        ? rec.coverage_details.reduce(
            (sum: number, c: any) => sum + (Number(c?.premimum) || 0),
            0
          )
        : null);
    const label = coverageDisplay || rec.vin;
    const isCoverageSubstitution = rec.mode
      ? rec.mode === "Change"
      : sectionIsCoverageChange;
    const mode = isCoverageSubstitution ? "CHANGE" : "ADD";
    const replacedCoverage =
      rec.replaced_record_details ??
      values.coverage_change?.delete_coverage_details;
    const replacedNumber =
      rec.replaced_delete_value ??
      replacedCoverage?.incremental_number ??
      values.coverage_change?.delete_coverage;
    const fields = buildFields([
      // The coverage being replaced (removed): number, name, VIN.
      ...(isCoverageSubstitution
        ? [
            {
              label: "Coverage to replace — number",
              to: replacedNumber,
              missing: false,
            },
            {
              label: "Coverage to replace — name",
              to: replacedCoverage?.coverage_name ?? null,
              missing: false,
            },
            {
              label: "Coverage to replace — VIN",
              to: replacedCoverage?.vin ?? null,
              missing: false,
            },
          ]
        : []),
      { label: isCoverageSubstitution ? "New coverage — name" : "Coverage", to: coverageDisplay },
      { label: isCoverageSubstitution ? "New coverage — VIN" : "VIN", to: rec.vin },
      {
        label: isCoverageSubstitution ? "New coverage — total premium" : "Total premium",
        to:
          derivedTotalPremium !== null && derivedTotalPremium !== undefined
            ? `$ ${derivedTotalPremium}`
            : null,
        // Premium is calculated/derived, not a required user input.
        missing: false,
      },
    ]);
    entries.push({
      id: `coverage_change-${index}`,
      section: "coverageChange",
      subType: "Coverage Change",
      mode,
      title: `Coverage ${label || `#${index + 1}`} — ${mode}`,
      fields,
      missingCount: countMissing(fields),
      recordIndex: index,
    });
  });
  if (
    !sectionIsCoverageChange &&
    asText(values.coverage_change?.delete_coverage) !== ""
  ) {
    const fields = buildFields([
      { label: "Coverage", to: values.coverage_change.delete_coverage, missing: false },
    ]);
    entries.push({
      id: "coverage_change-delete",
      section: "coverageChange",
      subType: "Coverage Change",
      mode: "DELETE",
      title: `Coverage ${values.coverage_change.delete_coverage} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Interested Party (record array) ---
  // A "Change" (substitution) replaces an existing interested party
  // (tracked in delete_interested_party) with a newly added record, so
  // those records show as CHANGE with the replaced party called out. Plain
  // additions still show as ADD.
  // Per-record mode (see Driver note above).
  const sectionIsInterestedPartyChange =
    values.interested_party_change?.type === "Change";
  (values.interested_party_change?.records || []).forEach(
    (rec: any, index: number) => {
      const isInterestedPartySubstitution = rec.mode
        ? rec.mode === "Change"
        : sectionIsInterestedPartyChange;
      const mode = isInterestedPartySubstitution ? "CHANGE" : "ADD";
      const replacedInterestedParty =
        rec.replaced_record_details ??
        values.interested_party_change?.delete_interested_party_details;
      const replacedNumber =
        rec.replaced_delete_value ??
        replacedInterestedParty?.number ??
        values.interested_party_change?.delete_interested_party;
      const fields = buildFields([
        // The interested party being replaced (removed): number, code, name.
        ...(isInterestedPartySubstitution
          ? [
              {
                label: "Party to replace — number",
                to: replacedNumber,
                missing: false,
              },
              {
                label: "Party to replace — code",
                to: replacedInterestedParty?.interested_party_code ?? null,
                missing: false,
              },
              {
                label: "Party to replace — name",
                to: replacedInterestedParty?.name ?? null,
                missing: false,
              },
            ]
          : []),
        // The new interested party being added in its place.
        { label: isInterestedPartySubstitution ? "New party — name" : "Name", to: rec.name },
        { label: isInterestedPartySubstitution ? "New party — interest" : "Interest", to: rec.interest },
        { label: isInterestedPartySubstitution ? "New party — address" : "Address", to: rec.address },
      ]);
      entries.push({
        id: `interested_party_change-${index}`,
        section: "interestedPartyChange",
        subType: "Interested Party Change",
        mode,
        title: `Interested party ${rec.name || `#${index + 1}`} — ${mode}`,
        fields,
        missingCount: countMissing(fields),
        recordIndex: index,
      });
    }
  );
  if (
    !sectionIsInterestedPartyChange &&
    asText(values.interested_party_change?.delete_interested_party) !== ""
  ) {
    const fields = buildFields([
      {
        label: "Interested party",
        to: values.interested_party_change.delete_interested_party,
        missing: false,
      },
    ]);
    entries.push({
      id: "interested_party_change-delete",
      section: "interestedPartyChange",
      subType: "Interested Party Change",
      mode: "DELETE",
      title: `Interested party ${values.interested_party_change.delete_interested_party} — DELETE`,
      fields,
      missingCount: 0,
    });
  }

  // --- Driver Assignment (record array) ---
  // Each record carries its own mode ("Add"/"Change") and, for Change, its
  // own substitution target — captured at save time — so relabeling one
  // record (e.g. clicking "Change" for a new record) never retroactively
  // changes earlier records' displayed mode.
  (values.driver_assignment_change?.records || []).forEach(
    (rec: any, index: number) => {
      const isSubstitution = rec.mode === "Change";
      const mode = isSubstitution ? "CHANGE" : "ADD";
      const label = [rec.vehicle_number, rec.driver_number]
        .filter((v) => v !== null && v !== undefined && v !== "")
        .join(" / ");
      const fields = buildFields([
        ...(isSubstitution
          ? [
              {
                label: "Assignment replaced",
                to: rec.delete_driver_assignment,
                missing: false,
              },
            ]
          : []),
        { label: "Vehicle", to: rec.vehicle_number },
        { label: "Driver", to: rec.driver_number },
        { label: "Assignment type", to: rec.assignment_type },
        { label: "Percentage", to: rec.percentage, missing: false },
        { label: "Notes", to: rec.notes, missing: false },
      ]);
      entries.push({
        id: `driver_assignment_change-${index}`,
        section: "driverAssignmentChange",
        subType: "Driver Assignment Change",
        mode,
        title: `Driver Assignment ${label || `#${index + 1}`} — ${mode}`,
        fields,
        missingCount: countMissing(fields),
        recordIndex: index,
      });
    }
  );

  return entries;
};
